import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { User, Certificate, UserProgress } from '../types';
import {
  User as UserIcon,
  Mail,
  Calendar,
  Award,
  TrendingUp,
  Download,
  Edit,
  Save,
  X,
} from 'lucide-react';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
  });
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [certsRes, progressRes] = await Promise.all([
        fetch('/api/certificates', { headers }),
        fetch('/api/progress/overall', { headers }),
      ]);

      if (certsRes.ok) {
        const data = await certsRes.json();
        setCertificates(data.certificates);
      }

      if (progressRes.ok) {
        const data = await progressRes.json();
        setOverallProgress(data.overallProgress);
      }
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to update profile');

      await refreshUser();
      setEditing(false);
      alert('Profil erfolgreich aktualisiert');
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Fehler beim Aktualisieren des Profils');
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (certId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/certificates/${certId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to download certificate');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zertifikat-${certId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download certificate:', error);
      alert('Fehler beim Herunterladen des Zertifikats');
    }
  };

  const getCertificateTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      year_completion: 'Lehrjahresabschluss',
      module_completion: 'Modulabschluss',
      lap_simulation: 'LAP-Simulation',
      lap_passed: 'LAP bestanden',
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Mein Profil</h1>
        <p className="mt-2 text-gray-600">
          Verwalte deine persönlichen Daten und Zertifikate
        </p>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Persönliche Daten
          </h2>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Bearbeiten
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    first_name: user?.first_name || '',
                    last_name: user?.last_name || '',
                    email: user?.email || '',
                  });
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Abbrechen
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                Speichern
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vorname
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nachname
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <UserIcon className="h-10 w-10 text-gray-400 mr-4" />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-lg font-medium text-gray-900">
                  {user?.first_name} {user?.last_name}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Mail className="h-10 w-10 text-gray-400 mr-4" />
              <div>
                <p className="text-sm text-gray-500">E-Mail</p>
                <p className="text-lg font-medium text-gray-900">
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="h-10 w-10 text-gray-400 mr-4" />
              <div>
                <p className="text-sm text-gray-500">Lehrjahr</p>
                <p className="text-lg font-medium text-gray-900">
                  {user?.current_year}. Lehrjahr
                </p>
              </div>
            </div>
            {user?.apprenticeship_start && (
              <div className="flex items-center">
                <Calendar className="h-10 w-10 text-gray-400 mr-4" />
                <div>
                  <p className="text-sm text-gray-500">Lehrbeginn</p>
                  <p className="text-lg font-medium text-gray-900">
                    {new Date(user.apprenticeship_start).toLocaleDateString(
                      'de-AT'
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overall Progress */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Gesamtfortschritt
          </h2>
          <TrendingUp className="h-6 w-6 text-primary-600" />
        </div>
        <div className="mb-2">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Beherrschung aller Themen</span>
            <span className="font-medium">{overallProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                overallProgress >= 80
                  ? 'bg-green-500'
                  : overallProgress >= 60
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`}
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {overallProgress >= 80
            ? 'Hervorragend! Du bist gut vorbereitet.'
            : overallProgress >= 60
            ? 'Guter Fortschritt! Weiter so.'
            : 'Noch etwas Übung nötig. Du schaffst das!'}
        </p>
      </div>

      {/* Certificates */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Meine Zertifikate
          </h2>
          <Award className="h-6 w-6 text-yellow-600" />
        </div>

        {certificates.length > 0 ? (
          <div className="space-y-4">
            {certificates.map((cert) => (
              <div
                key={cert.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      {getCertificateTypeLabel(cert.certificate_type)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Ausgestellt am:{' '}
                      {new Date(cert.issued_at).toLocaleDateString('de-AT')}
                    </p>
                    <p className="text-xs text-gray-500">
                      Verifizierungscode: {cert.verification_code}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => downloadCertificate(cert.id)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Award className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Keine Zertifikate
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Absolviere Tests, um Zertifikate zu erhalten
            </p>
          </div>
        )}
      </div>
    </div>
  );
}